import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const inp = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const lbl = "block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1";

const BLANK = {
  provider_name: "", provider_type: "Hospital", contact_name: "", phone: "", fax: "", email: "", address: "",
  first_visit_date: "", last_visit_date: "", diagnosis: "", treatment_status: "Ongoing",
  records_requested: false, records_received: false, billed_amount: "", has_lien: false, lien_amount: "", notes: "",
};

export default function MedicalProviderFormDialog({ open, onOpenChange, clientId, provider, meta, onSaved }) {
  const [v, setV] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [selClient, setSelClient] = useState("");
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  useEffect(() => {
    if (!open) return;
    if (!clientId && !provider) api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    setSelClient("");
    setV(provider ? {
      ...BLANK, ...provider,
      billed_amount: provider.billed_amount || "", lien_amount: provider.lien_amount || "",
    } : { ...BLANK, provider_type: meta.provider_types?.[0] || "Hospital" });
  }, [open, provider, meta, clientId]);

  const save = async () => {
    if (!v.provider_name.trim()) { toast.error("Add a provider / facility name"); return; }
    const targetClient = clientId || selClient;
    if (!provider && !targetClient) { toast.error("Select a case / client"); return; }
    setSaving(true);
    const body = { ...v, billed_amount: parseFloat(v.billed_amount) || 0, lien_amount: parseFloat(v.lien_amount) || 0 };
    try {
      if (provider) await api.put(`/medical-providers/${provider.id}`, body);
      else await api.post(`/clients/${targetClient}/medical-providers`, body);
      toast.success(provider ? "Provider updated" : "Provider added");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save provider");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="medical-provider-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{provider ? "Edit medical provider" : "New medical provider"}</DialogTitle>
          <DialogDescription className="sr-only">Add or edit a medical provider tied to this case.</DialogDescription>
        </DialogHeader>

        {!clientId && !provider && (
          <div className="mb-3">
            <label className={lbl}>Case / client</label>
            <Select value={selClient} onValueChange={setSelClient}>
              <SelectTrigger data-testid="mp-client-trigger" className="rounded-sm"><SelectValue placeholder="Select case…" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={lbl}>Provider / facility</label>
            <input data-testid="mp-name-input" className={inp} value={v.provider_name} onChange={(e) => set("provider_name", e.target.value)} placeholder="e.g. City General Hospital" />
          </div>
          <div>
            <label className={lbl}>Type</label>
            <Select value={v.provider_type} onValueChange={(x) => set("provider_type", x)}>
              <SelectTrigger data-testid="mp-type-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.provider_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div><label className={lbl}>Contact name</label><input className={inp} value={v.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div><label className={lbl}>Phone</label><input data-testid="mp-phone-input" className={inp} value={v.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className={lbl}>Fax</label><input className={inp} value={v.fax} onChange={(e) => set("fax", e.target.value)} /></div>
          <div><label className={lbl}>Email</label><input type="email" className={inp} value={v.email} onChange={(e) => set("email", e.target.value)} /></div>
        </div>

        <label className={lbl}>Address</label>
        <input className={inp + " mb-3"} value={v.address} onChange={(e) => set("address", e.target.value)} />

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <div><label className={lbl}>First visit</label><input type="date" className={inp} value={v.first_visit_date} onChange={(e) => set("first_visit_date", e.target.value)} /></div>
          <div><label className={lbl}>Last visit</label><input type="date" className={inp} value={v.last_visit_date} onChange={(e) => set("last_visit_date", e.target.value)} /></div>
          <div>
            <label className={lbl}>Treatment status</label>
            <Select value={v.treatment_status} onValueChange={(x) => set("treatment_status", x)}>
              <SelectTrigger data-testid="mp-status-trigger" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta.treatment_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <label className={lbl}>Diagnosis / injury</label>
        <textarea className={inp + " mb-3"} rows={2} value={v.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} />

        <div className="border border-gray-100 rounded-sm p-3 bg-gray-50/60 mb-3 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="mp-records-requested">
            <input type="checkbox" checked={v.records_requested} onChange={(e) => set("records_requested", e.target.checked)} className="accent-[#002FA7] h-4 w-4" /> Records requested
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="mp-records-received">
            <input type="checkbox" checked={v.records_received} onChange={(e) => set("records_received", e.target.checked)} className="accent-[#002FA7] h-4 w-4" /> Records received
          </label>
          <div><label className={lbl}>Billed ($)</label><input type="number" min="0" step="0.01" data-testid="mp-billed-input" className={inp} value={v.billed_amount} onChange={(e) => set("billed_amount", e.target.value)} /></div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2" data-testid="mp-lien-toggle">
              <input type="checkbox" checked={v.has_lien} onChange={(e) => set("has_lien", e.target.checked)} className="accent-[#002FA7] h-4 w-4" /> Lien
            </label>
            {v.has_lien && <div className="flex-1"><label className={lbl}>Lien ($)</label><input type="number" min="0" step="0.01" data-testid="mp-lien-amount" className={inp} value={v.lien_amount} onChange={(e) => set("lien_amount", e.target.value)} /></div>}
          </div>
        </div>

        <label className={lbl}>Notes</label>
        <textarea className={inp} rows={2} value={v.notes} onChange={(e) => set("notes", e.target.value)} />

        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mp-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : provider ? "Save changes" : "Add provider"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
